import { normalizePageType } from 'utils/builderTargeting';

/**
 * PD-35758: the live-CF status an offering transitions to at launch
 * (`testing` → `approved`). Used only for the preview override below.
 */
export const PREVIEW_LIVE_CF_STATUS = 'approved';

const PREVIEW_TTW_STATUSES = new Set(['testing', 'test_the_waters', 'pre_launch', 'ttw']);

/**
 * Preview-only status override for the TTW → CF campaign-page preview
 * (PD-35758). When marketing previews the CF page of an offering that is
 * still in a Test-The-Waters status (`?preview=true&previewPageType=CF`),
 * swap the status to the post-launch `approved` value so the whole page
 * renders as live CF — invest buttons instead of "Reserve Now", no TTW
 * banner / "Testing the Waters" badge / TTW disclaimer — for a filing
 * screenshot before the offering actually launches.
 *
 * Applied at the offering-data layer (the `getOffering` fetch + the
 * BuilderLayout cache seed) so EVERY consumer of `['offeringData', slug]`
 * sees the override — both `useGetOffering` callers and the many components
 * with their own inline `useQuery` on that key.
 *
 * Strictly gated: only fires in preview mode, only for the CF page type, and
 * only when the real status is a TTW status. It NEVER alters live rendering.
 *
 * PD-35901: legacy `?previewPageType=CF1` / `CF2` URLs continue to work —
 * `normalizePageType` aliases both to `CF`.
 */
export const applyPreviewStatusOverride = <T extends { status?: string } | null | undefined>(
  data: T,
  { preview, previewPageType }: { preview: boolean; previewPageType: string | null },
): T => {
  if (!data || !preview) return data;
  const pageType = normalizePageType(previewPageType);
  if (pageType !== 'CF') return data;
  const status = typeof data.status === 'string' ? data.status.toLowerCase() : '';
  if (!PREVIEW_TTW_STATUSES.has(status)) return data;
  return { ...data, status: PREVIEW_LIVE_CF_STATUS };
};

/**
 * Read the preview override flags from the current browser URL. Returns a
 * no-op (`preview: false`) on the server, where there's no URL to read — the
 * override is a client-only, preview-only concern.
 */
export const getPreviewOverrideFromLocation = (): {
  preview: boolean;
  previewPageType: string | null;
} => {
  if (typeof window === 'undefined') return { preview: false, previewPageType: null };
  const params = new URLSearchParams(window.location.search);
  return {
    preview: params.get('preview') === 'true',
    previewPageType: params.get('previewPageType'),
  };
};
